import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import UserAvatar from "../components/UserAvatar";
import PostCard from "../components/PostCard";
import { getModelName } from "../lib/aiModels";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { ArrowLeft, Loader2, UserPlus, UserCheck, Bot, Heart, FileText, Home, Search, Plus, Bell, User, X, Ban, VolumeX, Volume2 } from "lucide-react";
import api from "../lib/api";

export default function UserProfilePage() {
  const { username } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("posts");

  // Posts
  const [posts, setPosts] = useState([]);
  const [postsLoading, setPostsLoading] = useState(false);

  // Assistants
  const [assistants, setAssistants] = useState([]);

  // Likes
  const [likedPosts, setLikedPosts] = useState([]);
  const [likesLoading, setLikesLoading] = useState(false);

  // Follow
  const [isFollowing, setIsFollowing] = useState(false);
  const [followLoading, setFollowLoading] = useState(false);
  const [followHover, setFollowHover] = useState(false);

  // Block / Mute
  const [isBlocked, setIsBlocked] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [blockBusy, setBlockBusy] = useState(false);
  const [muteBusy, setMuteBusy] = useState(false);
  const [showBlockConfirm, setShowBlockConfirm] = useState(false);

  // Edit profile
  const [showEdit, setShowEdit] = useState(false);
  const [editName, setEditName] = useState("");
  const [editBio, setEditBio] = useState("");
  const [editSaving, setEditSaving] = useState(false);

  useEffect(() => {
    if (!username) return;
    setLoading(true);
    setError(null);
    api.get(`/profile/${username}`)
      .then((r) => {
        setProfile(r.data);
        setIsFollowing(r.data.is_following);
      })
      .catch(() => setError("User not found"))
      .finally(() => setLoading(false));
  }, [username]);

  // Load tab data
  useEffect(() => {
    if (!profile) return;
    if (activeTab === "posts") {
      setPostsLoading(true);
      api.get(`/posts/user/${username}?page=1&limit=20`)
        .then((r) => setPosts(r.data.posts))
        .catch(() => {})
        .finally(() => setPostsLoading(false));
    }
    if (activeTab === "assistants" && profile.is_own_profile) {
      api.get("/assistants").then((r) => setAssistants(r.data)).catch(() => {});
    }
    if (activeTab === "likes") {
      setLikesLoading(true);
      api.get(`/users/${username}/likes`)
        .then((r) => setLikedPosts(Array.isArray(r.data?.items) ? r.data.items : []))
        // 404 / errors fall through to the empty state below.
        .catch(() => setLikedPosts([]))
        .finally(() => setLikesLoading(false));
    }
  }, [activeTab, profile, username]);

  // Block/mute state ships on the profile payload itself.
  useEffect(() => {
    if (!profile || profile.is_own_profile) return;
    setIsBlocked(Boolean(profile.is_blocked));
    setIsMuted(Boolean(profile.is_muted));
  }, [profile]);

  const handleBlockToggle = async () => {
    if (blockBusy) return;
    setBlockBusy(true);
    try {
      if (isBlocked) {
        await api.delete(`/users/${username}/block`);
        setIsBlocked(false);
        toast.success(`Unblocked @${username}`);
      } else {
        await api.post(`/users/${username}/block`);
        setIsBlocked(true);
        setShowBlockConfirm(false);
        // Blocking severs the follow relationship in both directions.
        if (isFollowing) {
          setIsFollowing(false);
          setProfile((p) => ({ ...p, followers_count: Math.max(0, p.followers_count - 1) }));
        }
        toast.success(`Blocked @${username}`);
      }
    } catch { toast.error(isBlocked ? "Failed to unblock" : "Failed to block"); }
    finally { setBlockBusy(false); }
  };

  const handleMuteToggle = async () => {
    if (muteBusy) return;
    setMuteBusy(true);
    try {
      if (isMuted) {
        await api.delete(`/users/${username}/mute`);
        setIsMuted(false);
        toast.success(`Unmuted @${username}`);
      } else {
        await api.post(`/users/${username}/mute`);
        setIsMuted(true);
        toast.success(`Muted @${username}. Their posts won't appear in your feed.`);
      }
    } catch { toast.error(isMuted ? "Failed to unmute" : "Failed to mute"); }
    finally { setMuteBusy(false); }
  };

  const handleFollow = async () => {
    setFollowLoading(true);
    try {
      const r = await api.post(`/follow/${username}`);
      setIsFollowing(r.data.following);
      setProfile((p) => ({
        ...p,
        followers_count: r.data.following ? p.followers_count + 1 : Math.max(0, p.followers_count - 1)
      }));
      toast.success(r.data.following ? `Followed @${username}` : `Unfollowed @${username}`);
    } catch { toast.error("Failed"); }
    finally { setFollowLoading(false); }
  };

  const handleSaveProfile = async () => {
    setEditSaving(true);
    try {
      const r = await api.put("/profile/me", { display_name: editName.trim(), bio: editBio.trim() });
      setProfile((p) => ({ ...p, display_name: r.data.display_name, bio: r.data.bio }));
      setShowEdit(false);
      toast.success("Profile updated!");
    } catch { toast.error("Failed to update profile"); }
    finally { setEditSaving(false); }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <Loader2 className="w-6 h-6 text-cyan-400 animate-spin" />
    </div>
  );

  if (error || !profile) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center animate-fade-in-up">
        <p className="text-muted-foreground mb-4">{error || "User not found"}</p>
        <Button onClick={() => navigate("/feed")} variant="outline" className="border-border text-foreground/80 bg-card press-down">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Feed
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen pb-20 lg:pb-0" data-testid="user-profile-page">
      {/* Top Bar */}
      <div className="sticky top-0 z-30 nav-glass px-4 h-12 flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="text-muted-foreground hover:text-foreground/80 bg-transparent border-0 cursor-pointer" data-testid="profile-back-btn">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <p className="text-sm font-semibold text-foreground leading-tight">{profile.display_name}</p>
          <p className="text-[10px] text-muted-foreground/70">{profile.posts_count} posts</p>
        </div>
      </div>

      {/* Cover Photo */}
      <div className="h-44 bg-gradient-to-r from-[#0a3346] via-[#241566] to-[#46125e] border-b border-white/10 relative" data-testid="profile-cover">
        {profile.cover_photo_url && <img src={profile.cover_photo_url} alt="" className="w-full h-full object-cover" />}
      </div>

      {/* Profile Info */}
      <div className="max-w-2xl mx-auto px-4">
        <div className="flex items-end justify-between -mt-10 mb-4">
          <div className="neon-ring shadow-glow-sm rounded-full">
            <UserAvatar name={profile.display_name} username={profile.username} avatarUrl={profile.avatar_url} size="xl" />
          </div>
          <div className="flex gap-2 pb-2">
            {profile.is_own_profile ? (
              <Button variant="outline" onClick={() => { setEditName(profile.display_name); setEditBio(profile.bio || ""); setShowEdit(true); }} className="border-border text-foreground/80 bg-card h-8 text-xs px-4 rounded-full press-down" data-testid="edit-profile-btn">
                Edit Profile
              </Button>
            ) : (
              <>
              <Button
                variant="outline"
                onClick={handleMuteToggle}
                disabled={muteBusy}
                className={`h-8 text-xs px-3 rounded-full press-down bg-card ${isMuted ? "border-cyan-500/30 text-cyan-400" : "border-border text-foreground/80"}`}
                data-testid="mute-toggle-btn"
              >
                {muteBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : isMuted ? <><Volume2 className="w-3.5 h-3.5 mr-1" /> Unmute</> : <><VolumeX className="w-3.5 h-3.5 mr-1" /> Mute</>}
              </Button>
              <Button
                variant="outline"
                onClick={() => (isBlocked ? handleBlockToggle() : setShowBlockConfirm(true))}
                disabled={blockBusy}
                className={`h-8 text-xs px-3 rounded-full press-down ${isBlocked ? "border-red-500/40 text-red-400 bg-red-500/10 hover:bg-red-500/15" : "border-border text-foreground/80 bg-card"}`}
                data-testid="block-toggle-btn"
              >
                {blockBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : isBlocked ? "Unblock" : <><Ban className="w-3.5 h-3.5 mr-1" /> Block</>}
              </Button>
              <Button
                onClick={handleFollow}
                disabled={followLoading}
                onMouseEnter={() => isFollowing && setFollowHover(true)}
                onMouseLeave={() => setFollowHover(false)}
                className={`h-8 text-xs px-4 rounded-full press-down transition-all ${
                  isFollowing
                    ? followHover
                      ? "bg-red-500/10 border border-red-500/40 text-red-400 hover:bg-red-500/15"
                      : "bg-card border border-border text-foreground/80"
                    : "bg-gradient-to-r from-cyan-400 to-violet-500 text-white shadow-sm"
                }`}
                data-testid="follow-toggle-btn"
              >
                {followLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : isFollowing ? (followHover ? "Unfollow" : <><UserCheck className="w-3.5 h-3.5 mr-1" /> Following</>) : <><UserPlus className="w-3.5 h-3.5 mr-1" /> Follow</>}
              </Button>
              </>
            )}
          </div>
        </div>

        {/* Name, Bio */}
        <div className="mb-4">
          <h1 className="font-outfit text-xl font-semibold text-foreground" data-testid="profile-display-name">{profile.display_name}</h1>
          <p className="text-sm text-muted-foreground" data-testid="profile-username">@{profile.username}</p>
          {profile.bio && <p className="text-sm text-foreground/80 mt-2 leading-relaxed">{profile.bio}</p>}
        </div>

        {/* Stats */}
        <div className="flex gap-6 mb-6" data-testid="profile-stats">
          <div className="text-center">
            <span className="text-sm font-semibold text-foreground">{profile.posts_count}</span>
            <span className="text-xs text-muted-foreground ml-1">Posts</span>
          </div>
          <div className="text-center cursor-pointer hover:text-cyan-400">
            <span className="text-sm font-semibold text-foreground">{profile.followers_count}</span>
            <span className="text-xs text-muted-foreground ml-1">Followers</span>
          </div>
          <div className="text-center cursor-pointer hover:text-cyan-400">
            <span className="text-sm font-semibold text-foreground">{profile.following_count}</span>
            <span className="text-xs text-muted-foreground ml-1">Following</span>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-border/60 mb-4">
          {[
            { id: "posts", label: "Posts", icon: FileText },
            { id: "assistants", label: "AI Companions", icon: Bot },
            { id: "likes", label: "Likes", icon: Heart },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-4 py-3 text-xs font-medium border-b-2 transition-colors bg-transparent cursor-pointer border-0 ${
                activeTab === tab.id ? "border-b-indigo-500 text-cyan-400" : "border-b-transparent text-muted-foreground hover:text-foreground/80"
              }`}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="w-4 h-4" /> {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === "posts" && (
          <div className="space-y-4">
            {postsLoading ? (
              <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 text-cyan-400 animate-spin" /></div>
            ) : posts.length === 0 ? (
              <p className="text-center text-sm text-muted-foreground/70 py-8">No posts yet</p>
            ) : (
              posts.map((p) => <PostCard key={p.id} post={p} />)
            )}
          </div>
        )}

        {activeTab === "assistants" && (
          <div className="space-y-3">
            {assistants.length === 0 ? (
              <p className="text-center text-sm text-muted-foreground/70 py-8">No AI companions</p>
            ) : (
              assistants.map((a) => (
                <div key={a.id} className="bg-card rounded-xl border border-border/60 p-4 flex items-center gap-3 card-hover" data-testid={`profile-ai-${a.slug}`}>
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-cyan-400 to-violet-500 flex items-center justify-center text-white font-medium">{a.name?.charAt(0)}</div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground">{a.name}</p>
                    <p className="text-xs text-muted-foreground"><span className="capitalize">{a.purpose?.replace(/_/g, " ")}</span> &middot; {getModelName(a.ai_model)}</p>
                  </div>
                  <Link to={`/chat/${a.slug}`} className="text-xs font-medium text-cyan-400 no-underline hover:text-cyan-300">Chat</Link>
                </div>
              ))
            )}
          </div>
        )}

        {activeTab === "likes" && (
          <div className="space-y-4" data-testid="likes-tab">
            {likesLoading ? (
              <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 text-cyan-400 animate-spin" /></div>
            ) : likedPosts.length === 0 ? (
              <div className="text-center py-8" data-testid="likes-empty-state">
                <Heart className="w-6 h-6 text-muted-foreground/60 mx-auto mb-2" />
                <p className="text-sm text-muted-foreground/70">No liked posts yet</p>
              </div>
            ) : (
              likedPosts.map((p) => <PostCard key={p.id} post={p} />)
            )}
          </div>
        )}
      </div>

      {/* Mobile Bottom Tab Bar */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-card border-t border-border/60 h-14 flex items-center justify-around px-2">
        <Link to="/feed" className="flex flex-col items-center gap-0.5 no-underline text-muted-foreground/70"><Home className="w-5 h-5" /><span className="text-[10px]">Home</span></Link>
        <Link to="/explore" className="flex flex-col items-center gap-0.5 no-underline text-muted-foreground/70"><Search className="w-5 h-5" /><span className="text-[10px]">Explore</span></Link>
        <Link to="/feed" className="flex flex-col items-center gap-0.5 no-underline text-muted-foreground/70"><Plus className="w-5 h-5" /></Link>
        <Link to="/notifications" className="flex flex-col items-center gap-0.5 no-underline text-muted-foreground/70"><Bell className="w-5 h-5" /><span className="text-[10px]">Notifs</span></Link>
        <Link to={`/profile/${username}`} className="flex flex-col items-center gap-0.5 no-underline text-cyan-400"><User className="w-5 h-5" /><span className="text-[10px]">Profile</span></Link>
      </div>

      {/* Block Confirmation Modal */}
      {showBlockConfirm && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowBlockConfirm(false)} data-testid="block-confirm-modal">
          <div className="bg-card rounded-2xl shadow-xl max-w-sm w-full p-6 animate-fade-in-up" onClick={(e) => e.stopPropagation()}>
            <div className="w-12 h-12 mx-auto mb-4 rounded-xl bg-red-500/10 flex items-center justify-center"><Ban className="w-6 h-6 text-red-500" /></div>
            <h2 className="font-outfit text-lg font-semibold text-foreground text-center mb-2">Block @{username}?</h2>
            <p className="text-sm text-muted-foreground text-center mb-5">
              You won't see each other's posts or comments, and blocking <strong className="text-foreground/80">unfollows you from each other</strong>. They won't be notified.
            </p>
            <div className="flex gap-2">
              <button onClick={() => setShowBlockConfirm(false)} className="flex-1 px-4 py-2.5 text-sm text-muted-foreground bg-muted/70 rounded-lg border-0 cursor-pointer hover:bg-white/10">Cancel</button>
              <Button onClick={handleBlockToggle} disabled={blockBusy} className="flex-1 bg-red-500 hover:bg-red-600 text-white text-sm rounded-lg disabled:opacity-40" data-testid="confirm-block-btn">
                {blockBusy ? "Blocking..." : "Block"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Profile Modal */}
      {showEdit && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowEdit(false)} data-testid="edit-profile-modal">
          <div className="bg-card rounded-2xl shadow-xl max-w-md w-full p-6 animate-fade-in-up" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-outfit text-lg font-semibold text-foreground">Edit Profile</h2>
              <button onClick={() => setShowEdit(false)} className="text-muted-foreground/70 hover:text-muted-foreground bg-transparent border-0 cursor-pointer"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground/80 mb-1.5">Display Name</label>
                <input data-testid="edit-display-name" value={editName} onChange={(e) => setEditName(e.target.value)} maxLength={100} className="w-full px-4 py-2.5 bg-muted/40 border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-cyan-400/25 focus:border-cyan-400" />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground/80 mb-1.5">Bio</label>
                <textarea data-testid="edit-bio" value={editBio} onChange={(e) => setEditBio(e.target.value)} maxLength={300} rows={3} className="w-full px-4 py-2.5 bg-muted/40 border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-cyan-400/25 resize-none" />
                <p className="text-[11px] text-muted-foreground/70 mt-1 text-right">{editBio.length}/300</p>
              </div>
              <div className="flex gap-2 justify-end pt-2">
                <button onClick={() => setShowEdit(false)} className="px-4 py-2 text-xs text-muted-foreground bg-transparent border-0 cursor-pointer">Cancel</button>
                <Button onClick={handleSaveProfile} disabled={editSaving || !editName.trim()} className="bg-gradient-to-r from-cyan-400 to-violet-500 text-white text-xs px-5 h-9 rounded-lg disabled:opacity-40 press-down" data-testid="save-profile-btn">
                  {editSaving ? "Saving..." : "Save Changes"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
